import {
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  Timestamp,
  arrayUnion,
  arrayRemove,
  writeBatch
} from 'firebase/firestore';
import { onSnapshotWithRetry as onSnapshot } from '../utils/firestoreSubscribe';
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import { getDb, getStorage } from '../firebase';

export const createExpense = async (familyId, expenseData) => {
  const db = getDb();
  const expRef = doc(collection(db, 'families', familyId, 'expenses'));
  const data = {
    ...expenseData,
    owedAmount: expenseData.amount * (expenseData.splitRatio / 100),
    status: 'pending',
    settledAt: null,
    settledMethod: null,
    createdAt: Timestamp.now()
  };
  await setDoc(expRef, data);
  return { id: expRef.id, ...data };
};

export const updateExpense = async (familyId, expenseId, updates) => {
  const db = getDb();
  await updateDoc(doc(db, 'families', familyId, 'expenses', expenseId), updates);
};

export const updateExpenseWithAudit = async (familyId, expenseId, updates, previousExpense, currentUser) => {
  const db = getDb();
  const onlyChildIds = Object.keys(updates).every((k) => k === 'childIds');
  if (previousExpense.status === 'settled' && !onlyChildIds) {
    throw new Error('Settled expenses cannot be edited');
  }

  const changes = [];
  const fieldLabels = {
    description: 'Description',
    amount: 'Amount',
    date: 'Date',
    category: 'Category',
    childIds: 'Child',
    paidBy: 'Paid by',
    splitRatio: 'Split ratio',
    note: 'Note',
    receiptURL: 'Receipt',
  };

  for (const [key, label] of Object.entries(fieldLabels)) {
    const newVal = updates[key];
    let oldVal = previousExpense[key];

    if (newVal === undefined) continue;

    if (key === 'date') {
      if (oldVal?.toDate) oldVal = oldVal.toDate();
      if (oldVal instanceof Date && newVal instanceof Date) {
        if (oldVal.getTime() === newVal.getTime()) continue;
      }
    }
    else if (key === 'childIds') {
      const oldArr = (oldVal || []).sort().join(',');
      const newArr = (newVal || []).sort().join(',');
      if (oldArr === newArr) continue;
    }
    else if (key === 'owedAmount') continue;
    else {
      if (String(oldVal ?? '') === String(newVal ?? '')) continue;
    }

    changes.push({ field: label, from: oldVal, to: newVal });
  }

  if (!changes.length) {
    await updateDoc(doc(db, 'families', familyId, 'expenses', expenseId), updates);
    return;
  }

  const auditEntry = {
    editedBy: currentUser.uid,
    editedByName: currentUser.displayName || 'Unknown',
    editedAt: Timestamp.now(),
    changes,
  };

  await updateDoc(doc(db, 'families', familyId, 'expenses', expenseId), {
    ...updates,
    editHistory: arrayUnion(auditEntry),
  });
};

export const deleteExpense = async (familyId, expenseId) => {
  const db = getDb();
  await deleteDoc(doc(db, 'families', familyId, 'expenses', expenseId));
};

const createPaymentRecord = async (familyId, paymentData) => {
  const db = getDb();
  try {
    const payRef = doc(collection(db, 'families', familyId, 'payments'));
    await setDoc(payRef, { ...paymentData, createdAt: Timestamp.now() });
  } catch (error) {
    console.warn('Failed to create payment record:', error);
  }
};

export const settleExpense = async (familyId, expenseId, method, { paidBy, paidTo, amount, createdBy }) => {
  const db = getDb();
  const now = Timestamp.now();

  await updateDoc(doc(db, 'families', familyId, 'expenses', expenseId), {
    status: 'settled',
    settledAt: now,
    settledMethod: method
  });

  await createPaymentRecord(familyId, {
    expenseIds: [expenseId],
    method,
    totalAmount: amount,
    paidBy,
    paidTo,
    createdBy
  });
};

export const settleExpensesBulk = async (familyId, expenseIds, method, { paidBy, paidTo, totalAmount, createdBy }) => {
  const db = getDb();
  const batch = writeBatch(db);
  const now = Timestamp.now();

  expenseIds.forEach(id => {
    batch.update(doc(db, 'families', familyId, 'expenses', id), {
      status: 'settled',
      settledAt: now,
      settledMethod: method
    });
  });

  await batch.commit();

  await createPaymentRecord(familyId, {
    expenseIds,
    method,
    totalAmount,
    paidBy,
    paidTo,
    createdBy
  });
};

export const subscribeToExpenses = (familyId, callback) => {
  const db = getDb();
  const q = query(
    collection(db, 'families', familyId, 'expenses'),
    orderBy('date', 'desc')
  );
  return onSnapshot(q, (snapshot) => {
    const expenses = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(expenses);
  }, (error) => {
    console.error('Expenses listener error:', error);
  });
};

export const subscribeToPayments = (familyId, callback) => {
  const db = getDb();
  const q = query(
    collection(db, 'families', familyId, 'payments'),
    orderBy('createdAt', 'desc')
  );
  return onSnapshot(q, (snapshot) => {
    const payments = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(payments);
  }, (error) => {
    console.error('Payments listener error:', error);
  });
};

export const deleteDuplicateActivityExpenses = async (familyId) => {
  const db = getDb();
  const q = query(
    collection(db, 'families', familyId, 'expenses'),
    where('autoCreated', '==', true)
  );
  const snapshot = await getDocs(q);

  const groups = {};
  snapshot.docs.forEach(d => {
    const data = d.data();
    if (!data.activityId || !data.activityDate) return;
    const key = `${data.activityId}:${data.activityDate}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push({ id: d.id, createdAt: data.createdAt });
  });

  const toDelete = [];
  Object.values(groups).forEach(docs => {
    if (docs.length <= 1) return;
    docs.sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
    toDelete.push(...docs.slice(1).map(d => d.id));
  });

  if (!toDelete.length) return 0;

  for (let i = 0; i < toDelete.length; i += 500) {
    const batch = writeBatch(db);
    toDelete.slice(i, i + 500).forEach(id => {
      batch.delete(doc(db, 'families', familyId, 'expenses', id));
    });
    await batch.commit();
  }

  return toDelete.length;
};

export const createRecurringExpense = async (familyId, data) => {
  const db = getDb();
  const recRef = doc(collection(db, 'families', familyId, 'recurringExpenses'));
  const record = {
    ...data,
    isActive: true,
    createdAt: Timestamp.now()
  };
  await setDoc(recRef, record);
  return { id: recRef.id, ...record };
};

export const updateRecurringExpense = async (familyId, id, updates) => {
  const db = getDb();
  await updateDoc(doc(db, 'families', familyId, 'recurringExpenses', id), updates);
};

export const deleteRecurringExpense = async (familyId, id) => {
  const db = getDb();
  await deleteDoc(doc(db, 'families', familyId, 'recurringExpenses', id));
};

export const subscribeToRecurringExpenses = (familyId, callback) => {
  const db = getDb();
  const q = query(
    collection(db, 'families', familyId, 'recurringExpenses'),
    orderBy('createdAt', 'desc')
  );
  return onSnapshot(q, (snapshot) => {
    const items = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(items);
  }, (error) => {
    console.error('Recurring expenses listener error:', error);
  });
};

export const calculateBalance = (expenses, parentIds, householdMembers = {}) => {
  const balance = { [parentIds[0]]: 0, [parentIds[1]]: 0 };

  const resolveToParent = (id) => {
    if (parentIds.includes(id)) return id;
    const member = householdMembers[id];
    if (member?.householdOf && parentIds.includes(member.householdOf)) {
      return member.householdOf;
    }
    return id;
  };

  expenses.forEach(expense => {
    if (expense.status === 'settled') return;
    const paidBy = resolveToParent(expense.paidBy);
    const otherParent = parentIds.find(id => id !== paidBy);
    if (otherParent) {
      balance[otherParent] += expense.owedAmount;
    }
  });

  const netOwed = balance[parentIds[0]] - balance[parentIds[1]];
  if (netOwed > 0) {
    return { owerId: parentIds[0], oweeId: parentIds[1], amount: netOwed };
  } else if (netOwed < 0) {
    return { owerId: parentIds[1], oweeId: parentIds[0], amount: Math.abs(netOwed) };
  }
  return { owerId: null, oweeId: null, amount: 0 };
};

export const getExpenseReceipts = (expense) => {
  if (expense.receipts?.length) return expense.receipts;
  if (expense.receiptURL) {
    return [{ url: expense.receiptURL, storagePath: expense.receiptStoragePath, fileName: expense.receiptFileName }];
  }
  return [];
};

export const uploadReceipt = async (familyId, expenseId, file, optionsOrOnProgress) => {
  const db = getDb();
  const storage = getStorage();
  const options = typeof optionsOrOnProgress === 'function'
    ? { onProgress: optionsOrOnProgress }
    : (optionsOrOnProgress || {});
  const fileName = options.fileName || file.name;
  const onProgress = options.onProgress;
  const storagePath = `families/${familyId}/expenses/${expenseId}/receipt/${Date.now()}_${fileName}`;
  const storageRef = ref(storage, storagePath);

  return new Promise((resolve, reject) => {
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        if (onProgress) onProgress(progress);
      },
      (error) => reject(error),
      async () => {
        try {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          const receiptObj = { url, storagePath, fileName };
          await updateDoc(doc(db, 'families', familyId, 'expenses', expenseId), {
            receipts: arrayUnion(receiptObj),
          });
          resolve(receiptObj);
        } catch (error) {
          reject(error);
        }
      }
    );
  });
};

export const deleteReceipt = async (familyId, expenseId, receiptObj) => {
  const db = getDb();
  const storage = getStorage();
  try {
    await deleteObject(ref(storage, receiptObj.storagePath));
  } catch (error) {
    console.warn('Receipt file already deleted or not found:', error);
  }
  await updateDoc(doc(db, 'families', familyId, 'expenses', expenseId), {
    receipts: arrayRemove(receiptObj),
  });
};
